import { useEffect, useState } from "react";
import { OpsanioLocationValue } from "src/questionnaires";

/** Public OSM raster tiles - the single source for every Leaflet map. */
export const TILE_LAYER_URL =
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

export const TILE_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** One concrete low-zoom tile, used to probe whether tiles are served to us. */
const PROBE_TILE_URL = "https://a.tile.openstreetmap.org/5/16/10.png";

const PROBE_TIMEOUT_MS = 8000;

export type TileStatus = "checking" | "available" | "blocked";

export interface TileAvailability {
  status: TileStatus;
  /** HTTP status of the probe when it could be read (403 = tiles refused). */
  httpStatus?: number;
}

let probeResult: Promise<TileAvailability> | null = null;

/**
 * `fetch` tells us the exact status (403 vs 200) but only when the response
 * carries CORS headers - a refusal page usually does not, and the request then
 * rejects without a status. `null` means "inconclusive, try the image probe".
 */
const probeViaFetch = async (): Promise<TileAvailability | null> => {
  try {
    const res = await fetch(PROBE_TILE_URL, {
      method: "GET",
      cache: "force-cache",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return {
      status: res.ok ? "available" : "blocked",
      httpStatus: res.status,
    };
  } catch {
    return null;
  }
};

/**
 * `<img>` loads are not subject to CORS, so this always resolves: a 403 (or any
 * other refusal) fires `onerror`. It cannot report the status code - but for the
 * decision at hand ("can we render tiles?") a failure is a failure.
 */
const probeViaImage = (): Promise<TileAvailability> =>
  new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const done = (status: TileStatus) => {
      if (settled) return;
      settled = true;
      img.onload = null;
      img.onerror = null;
      resolve({ status });
    };
    img.onload = () => done("available");
    img.onerror = () => done("blocked");
    img.src = PROBE_TILE_URL;
    setTimeout(() => done("blocked"), PROBE_TIMEOUT_MS);
  });

/** Probe once per page load; every map shares the same answer. */
export const probeMapTiles = (): Promise<TileAvailability> => {
  if (!probeResult) {
    probeResult = probeViaFetch().then((res) => res ?? probeViaImage());
  }
  return probeResult;
};

/**
 * Whether the OSM tile server actually serves us tiles. A map whose tiles are
 * refused (403) must not be rendered at all - an empty grey grid with an
 * attribution line is worse than no map, so callers hide the container while
 * this returns "blocked".
 */
export const useMapTiles = (): TileAvailability => {
  const [availability, setAvailability] = useState<TileAvailability>({
    status: "checking",
  });

  useEffect(() => {
    let active = true;
    probeMapTiles().then((res) => {
      if (active) setAvailability(res);
    });
    return () => {
      active = false;
    };
  }, []);

  return availability;
};

/**
 * Manually entered addresses may have no coordinates - everything that puts a
 * pin on a map or builds a maps link has to check first.
 */
export const hasCoordinates = (
  location?: Pick<OpsanioLocationValue, "lat" | "lng">,
): boolean =>
  !!location &&
  typeof location.lat === "number" &&
  typeof location.lng === "number" &&
  Number.isFinite(location.lat) &&
  Number.isFinite(location.lng) &&
  location.lat >= -90 &&
  location.lat <= 90 &&
  location.lng >= -180 &&
  location.lng <= 180;
